import {
  ArrowBack,
  Chat as ChatIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ChatBottomNav from '../components/chat/ChatBottomNav';
import ChatMessageBubble from '../components/chat/ChatMessageBubble';
import ConfirmDialog from '../components/ConfirmDialog';
import AppHeader from '../components/layout/AppHeader';
import API_ENDPOINTS from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../hooks/useConfirm';

// Types
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface InsightsGenerated {
  summary_text?: string;
  key_topics?: string[];
  mood_analysis?: {
    overall_sentiment?: string;
    key_emotions?: string[];
    emotional_progression?: string;
  };
  insights?: {
    action_items?: string[];
    recommendations?: string[];
    unresolved_issues?: string[];
  };
  generated_at?: string;
}

interface Conversation {
  id: string;
  title: string;
  is_active: boolean;
  is_archived: boolean;
  is_pinned: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  ai_analysis?: {
    topics_discussed?: string[];
    insights_generated?: InsightsGenerated;
    mood_trend?: Record<string, unknown>;
    conversation_type?: string;
    status?: string;
  };
}

const ChatArchivePage: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const { confirmState, confirm, handleConfirm, handleCancel } = useConfirm();

  // State for conversation list
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<string>('');

  // State for individual conversation view
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_ENDPOINTS.CONVERSATIONS.LIST}?ordering=-created_at`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Show completed, non-archived conversations in the archive
      const completedConversations = (response.data.results || []).filter(
        (conv: Conversation) => !conv.is_active && !conv.is_archived
      );
      // Reason: pinned conversations appear first, then date order
      completedConversations.sort((a: Conversation, b: Conversation) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setConversations(completedConversations);
    } catch (error: any) {
      console.error('Failed to load conversations:', error);
      setError('Failed to load conversation history');
    } finally {
      setLoading(false);
    }
  };

  const loadConversationDetails = async (convId: string) => {
    try {
      setMessagesLoading(true);

      // Load conversation details
      const convResponse = await axios.get(`${API_ENDPOINTS.CONVERSATIONS.LIST}${convId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setSelectedConversation(convResponse.data);

      // Load messages
      const messagesResponse = await axios.get(API_ENDPOINTS.CONVERSATIONS.MESSAGES(convId), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessages(messagesResponse.data.results || []);
    } catch (error: any) {
      console.error('Failed to load conversation details:', error);
      setError('Failed to load conversation details');
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadConversationsCallback = useCallback(loadConversations, [token]);
  const loadConversationDetailsCallback = useCallback(loadConversationDetails, [token]);

  useEffect(() => {
    loadConversationsCallback();
  }, [loadConversationsCallback]);

  useEffect(() => {
    if (conversationId) {
      loadConversationDetailsCallback(conversationId);
    }
  }, [conversationId, loadConversationDetailsCallback]);

  const handleConversationSelect = (convId: string) => {
    navigate(`/chat-archive/${convId}`);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateShort = (dateString: string): string => {
    const date = new Date(dateString);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  };

  const handleToggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = (): void => {
    if (selectedIds.size === conversations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(conversations.map((c) => c.id)));
    }
  };

  // Reason: replace generic AI-generated references with personalised names
  const personalizeSummary = (text: string): string => {
    const userName = user?.display_name?.trim() || user?.name?.trim() || 'You';
    const assistantName = user?.voice_gender === 'female' ? 'Jess' : 'Jeff';
    return text
      .replace(/\bThe user\b/gi, userName)
      .replace(/\bThe assistant\b/gi, assistantName);
  };

  const handleBulkAction = async (action: string): Promise<void> => {
    setBulkAction(action);
    if (action === 'delete' && selectedIds.size > 0) {
      const confirmed = await confirm({
        title: 'Delete conversations?',
        message: `Are you sure you want to delete ${selectedIds.size} conversation(s)? This cannot be undone.`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) {
        setBulkAction('');
        return;
      }
      try {
        // Archive each selected conversation
        await Promise.all(
          Array.from(selectedIds).map((id) =>
            axios.post(API_ENDPOINTS.CONVERSATIONS.ARCHIVE(id), {}, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            })
          )
        );
        // Remove archived conversations from the list and clear selection
        setConversations((prev) => prev.filter((c) => !selectedIds.has(c.id)));
        setSelectedIds(new Set());
      } catch (err) {
        console.error('Failed to archive conversations:', err);
        setError('Failed to delete selected conversations');
      }
    }
    if (action === 'pin' && selectedIds.size > 0) {
      try {
        // Determine if all selected are already pinned (then unpin), else pin
        const allPinned = Array.from(selectedIds).every(
          (id) => conversations.find((c) => c.id === id)?.is_pinned
        );
        await Promise.all(
          Array.from(selectedIds).map((id) => {
            const url = allPinned
              ? API_ENDPOINTS.CONVERSATIONS.UNPIN(id)
              : API_ENDPOINTS.CONVERSATIONS.PIN(id);
            return axios.post(url, {}, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
          })
        );
        // Update local state
        setConversations((prev) => {
          const updated = prev.map((c) =>
            selectedIds.has(c.id) ? { ...c, is_pinned: !allPinned } : c
          );
          updated.sort((a, b) => {
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
          return updated;
        });
        setSelectedIds(new Set());
      } catch (err) {
        console.error('Failed to pin/unpin conversations:', err);
        setError('Failed to pin/unpin selected conversations');
      }
    }
    setBulkAction('');
  };

  // If viewing a specific conversation
  if (conversationId && selectedConversation) {
    return (
      <Box sx={{ height: 'var(--app-height, 100vh)', display: 'flex', flexDirection: 'column' }}>
        <AppHeader
          title="Chat Archive"
          leftContent={
            <IconButton
              aria-label="Back to archive"
              onClick={() => navigate('/chat-archive')}
              sx={{
                width: 48,
                height: 44,
                bgcolor: '#AFAFAF',
                border: '1px solid #AFAFAF',
                borderRadius: '32px',
                '&:hover': { bgcolor: '#9E9E9E' },
              }}
            >
              <ArrowBack sx={{ color: '#FFFFFF' }} />
            </IconButton>
          }
        />

        {messagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#FFFFFF' }}>
            {/* AI Insights Summary */}
            {(() => {
              const insights = selectedConversation.ai_analysis?.insights_generated;
              if (!insights || !insights.summary_text) return null;
              const topics = selectedConversation.ai_analysis?.topics_discussed || [];
              const actionItems = insights.insights?.action_items?.filter(Boolean) || [];
              const recommendations = insights.insights?.recommendations?.filter(Boolean) || [];
              return (
                <Card sx={{ m: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Conversation Summary ({formatDate(selectedConversation.created_at)})
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                      {personalizeSummary(insights.summary_text)}
                    </Typography>
                    {topics.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                          Topics:
                        </Typography>
                        {topics.map((topic, idx) => (
                          <Chip
                            key={idx}
                            label={topic}
                            size="small"
                            sx={{
                              height: 22,
                              bgcolor: '#FFD400',
                              color: '#333',
                              textTransform: 'capitalize',
                              '& .MuiChip-label': { px: 1, fontSize: 11 },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    {actionItems.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                          Suggested actions:
                        </Typography>
                        {actionItems.map((item, idx) => (
                          <Typography key={idx} variant="body2" sx={{ pl: 1.5, fontSize: 13 }}>
                            • {item}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {recommendations.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                          Recommendations:
                        </Typography>
                        {recommendations.map((rec, idx) => (
                          <Typography key={idx} variant="body2" sx={{ pl: 1.5, fontSize: 13 }}>
                            • {rec}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Messages - using ChatMessageBubble for consistent styling */}
            <Box sx={{ pb: 4 }}>
              {messages.map((message) => (
                <ChatMessageBubble
                  key={message.id}
                  content={message.content}
                  createdAt={message.created_at}
                  role={message.role}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Main archive list view
  return (
    <Box
      sx={{
        height: 'var(--app-height, 100vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
      }}
    >
      <AppHeader
        title="Chat Archive"
        leftContent={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={
                conversations.length > 0 &&
                selectedIds.size === conversations.length
              }
              indeterminate={
                selectedIds.size > 0 &&
                selectedIds.size < conversations.length
              }
              onChange={handleSelectAll}
              sx={{ '& .MuiSvgIcon-root': { color: '#FFFFFF' } }}
            />
            <Typography
              variant="body2"
              sx={{ color: '#828282', fontWeight: 600, mr: 0.5 }}
            >
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : 'Select all'}
            </Typography>
            {selectedIds.size > 0 && (
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={bulkAction}
                  displayEmpty
                  onChange={(e) => handleBulkAction(e.target.value)}
                  sx={{
                    borderRadius: '10px',
                    fontSize: 13,
                    height: 32,
                    bgcolor: '#FFFFFF',
                    '& .MuiSelect-select': { py: 0.5 },
                  }}
                >
                  <MenuItem value="" disabled>
                    Actions
                  </MenuItem>
                  <MenuItem value="delete">Delete</MenuItem>
                  <MenuItem value="pin">
                    {Array.from(selectedIds).every(
                      (id) => conversations.find((c) => c.id === id)?.is_pinned
                    ) ? 'Unpin' : 'Pin'}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        }
      />

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : conversations.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No conversations yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start a new conversation to see it appear here.
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => navigate('/')}
              >
                Start New Chat
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ pt: 1 }}>
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 0 }}>
              {conversations.map((conversation) => {
                const insights = conversation.ai_analysis?.insights_generated;
                const summaryText = insights?.summary_text || '';
                const topics = conversation.ai_analysis?.topics_discussed || [];
                const dateShort = formatDateShort(conversation.created_at);
                const isSelected = selectedIds.has(conversation.id);

                return (
                  <ListItem
                    key={conversation.id}
                    disableGutters
                    sx={{ p: 0 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      {/* Left column: checkbox + date */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          flexShrink: 0,
                          pt: 1,
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleSelect(conversation.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ '& .MuiSvgIcon-root': { color: '#AFAFAF' } }}
                        />
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#828282',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {dateShort}
                        </Typography>
                      </Box>

                      {/* Right: white container with yellow border */}
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => handleConversationSelect(conversation.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleConversationSelect(conversation.id);
                          }
                        }}
                        sx={{
                          flex: 1,
                          bgcolor: isSelected ? '#D0D0D0' : '#E8E8E8',
                          border: 'none',
                          borderRadius: '25px',
                          px: 2,
                          py: 1.75,
                          minHeight: 57,
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          outline: 'none',
                          filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.1))',
                          '&:hover': {
                            bgcolor: 'rgba(255, 212, 0, 0.08)',
                          },
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          {topics.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: '#666',
                                  mr: 0.25,
                                }}
                              >
                                Topics:
                              </Typography>
                              {topics.map((topic, idx) => (
                                <Chip
                                  key={idx}
                                  label={topic}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    bgcolor: '#FFD400',
                                    color: '#333',
                                    textTransform: 'capitalize',
                                    '& .MuiChip-label': { px: 0.75, fontSize: 10 },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                          {summaryText ? (
                            <Typography
                              sx={{
                                fontFamily:
                                  'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                                fontSize: 13,
                                color: '#333333',
                                fontWeight: conversation.is_pinned ? 700 : 400,
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {personalizeSummary(summaryText)}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                fontFamily:
                                  'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                                fontSize: 14,
                                color: '#828282',
                                fontStyle: 'italic',
                              }}
                            >
                              No summary available
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </Box>

      <ChatBottomNav />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Box>
  );
};

export default ChatArchivePage;
